import { authQueryKeys } from '@/config/queryKeys';
import { server } from '@/constants';
import type { User } from '@/types';
import { queryOptions, useQuery } from '@tanstack/react-query';

// Auth query timing constants
const AUTH_STALE_TIME_MS = 5 * 60 * 1000; // Consider auth data fresh for 5 minutes
const AUTH_REFETCH_INTERVAL_MS = 5 * 60 * 1000; // Recheck auth status every 5 minutes

function authOptions(props?: Partial<ReturnType<typeof queryOptions<User | null>>> & { suspense?: boolean }) {
  const suspense = props?.suspense ?? false;
  // Remove suspense from props so it doesn't get spread into queryOptions
  const { suspense: _suspense, ...restProps } = props || {};
  return queryOptions<User | null>({
    queryKey: authQueryKeys.me(),
    queryFn: async () => {
      // Use fetch directly for auth query to avoid redirect loops
      // The auth query should not redirect - it's checking auth status
      const response = await fetch(`${server}/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Don't throw on 401 - just return null to indicate not authenticated
      // The redirect is handled by useAuthStatus hook when it detects the transition
      // from authenticated to unauthenticated state (session expiration)
      if (response.status === 401) {
        return null;
      }

      // Handle 403 (forbidden)
      if (response.status === 403) {
        throw new Error('FORBIDDEN');
      }

      if (!response.ok) {
        throw new Error(`Auth check failed: ${response.status}`);
      }

      return response.json();
    },
    staleTime: AUTH_STALE_TIME_MS,
    refetchInterval: AUTH_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true, // Recheck when user returns to the tab
    refetchOnMount: true, // Recheck when component mounts
    ...(suspense
      ? {}
      : {
          throwOnError: false, // Don't throw on error - handle gracefully
          retry: false,
        }),
    ...restProps,
  });
}

function useAuthQuery(props?: Parameters<typeof authOptions>[0]) {
  return useQuery(authOptions(props));
}

export { authOptions, useAuthQuery };
